import React, { useState, useEffect, useRef } from "react";
import useDecode from "../../../hooks/useDecode";
import { useNavigate, useParams } from "react-router-dom";
import { Joyride } from 'react-joyride';
import { getJoyrideScrollOffset } from 'utils/joyrideConfig';
import PageHeader from "../../blocks/PageHeader/PageHeader";
import LoaderMin from "components/blocks/Loader/LoaderMin";
import Icon from "../../blocks/Icon/Icon";
import CommBubble from "../../blocks/CommBubble/CommBubble";
import useCreateGeneralCommunication from "services/communications/useCreateGeneralCommunication";
import useGetAllUserCommunicationByUserId from "../../../services/communications/useGetAllUserCommunicationByUserId";
import useGetRole from "hooks/useGetRole";
import { useCommunicationChatTour } from "hooks/useCommunicationChatTour";
import "./CommunicationChatGeneral.scss";

const JOYRIDE_PROPS = {
  continuous: true,
  scrollToFirstStep: true,
  locale: { back: 'Anterior', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', nextWithProgress: 'Siguiente ({current} de {total})', open: 'Iniciar tour de ayuda', skip: 'Omitir tour' },
  options: { primaryColor: '#0F1F3D', zIndex: 10000, overlayColor: 'rgba(15,47,73,0.45)', showProgress: true, buttons: ['back', 'skip', 'close', 'primary'] } as any,
  styles: { beaconInner: { backgroundColor: '#F97316' }, beaconOuter: { borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' }, tooltip: { borderRadius: 8, fontSize: 14 }, tooltipTitle: { fontSize: 15, fontWeight: 700, textAlign: 'left' as const }, tooltipContent: { textAlign: 'left' as const } },
};

interface IdataComunication {
  externalUserId: string;
  userId: string;
  prestacionId: string | null;
  asunto: string;
  mensaje: string;
  files: any[];
  comunicacionReferenciaId: string | null;
}

const formatDateEs = (dateStr: string): string => {
  const d = new Date(dateStr);
  const weekday = d.toLocaleDateString("es-ES", { weekday: "long" });
  const day = d.getDate();
  const month = d.toLocaleDateString("es-ES", { month: "long" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${day} de ${month} de ${year} · ${hh}:${mm}h`;
};

const CommunicationChatGeneral: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const communicationId = id;
  const tokenResponse = useDecode("token");
  const externalUserId = tokenResponse?.extension_external_id;
  const userId = tokenResponse && tokenResponse.extension_id;
  const [userTokenId, setUserTokenId] = useState("");
  const { isImpersonation } = useGetRole;
  const isImpersonating = isImpersonation();

  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const rfileRef = useRef<HTMLInputElement>(null);

  const { getUserCommunicationId, allUserCommunication, loadingGetAllUserCommunication } =
    useGetAllUserCommunicationByUserId();
  const { createGeneralCommunication, successCreateGeneralCommunication } =
    useCreateGeneralCommunication();

  const dataCommunicationChat =
    allUserCommunication?.find((c: any) => c.id === communicationId) ?? null;
  const canReply = !!(dataCommunicationChat?.puedeResponder && !isImpersonating);
  const { run, tourKey, steps, handleCallback, startTour } = useCommunicationChatTour(canReply);

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('solimat:startCommunicationChatTour', handler);
    return () => window.removeEventListener('solimat:startCommunicationChatTour', handler);
  }, [startTour]);

  useEffect(() => {
    if (tokenResponse !== undefined) {
      setUserTokenId(tokenResponse.extension_id);
    }
    getUserCommunicationId(userId, communicationId);
  }, [successCreateGeneralCommunication, isImpersonating]);

  useEffect(() => {
    if (allUserCommunication) {
      // No-op — kept for future side effects
    }
  }, [allUserCommunication]);

  const replies: any[] =
    dataCommunicationChat?.comunicaciones ||
    dataCommunicationChat?.respuestas ||
    [];

  const convertBase64 = async (files: File[]): Promise<any[]> => {
    if (!files || files.length === 0) return [];
    return Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              const parts = file.name.split(".");
              resolve({
                name: file.name,
                extension: parts.pop(),
                externalUri: "",
                base64: (reader.result as string).split(",")[1],
                externalId: "",
              });
            };
            reader.onerror = reject;
          })
      )
    );
  };

  const attach = () => rfileRef.current?.click();

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const captured = Array.from(e.target.files);
      e.target.value = "";
      setReplyFiles((prev) => [...prev, ...captured]);
    }
  };

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    const dataFiles = await convertBase64(replyFiles);
    const newData: IdataComunication = {
      externalUserId,
      userId,
      prestacionId: dataCommunicationChat?.prestacionId ?? null,
      asunto: dataCommunicationChat?.asunto ?? "",
      mensaje: replyText,
      files: dataFiles,
      comunicacionReferenciaId: dataCommunicationChat?.puedeResponder
        ? dataCommunicationChat?.id
        : null,
    };
    createGeneralCommunication(userId, newData, () => {
      getUserCommunicationId(userId, communicationId);
      setReplyText("");
      setReplyFiles([]);
      setShowReply(false);
    });
  };

  return (
    <>
      <Joyride
        key={tourKey}
        steps={steps}
        run={run}
        onEvent={handleCallback}
        {...JOYRIDE_PROPS}
        scrollOffset={getJoyrideScrollOffset()}
      />
      {loadingGetAllUserCommunication ? (
        <section className="container">
          <LoaderMin />
        </section>
      ) : (
        <>
          <div className="container">
            <PageHeader
              title={dataCommunicationChat?.asunto || "Comunicación"}
              subtitle={
                dataCommunicationChat && (
                  <>
                    Enviado por:{" "}
                    <strong>
                      {dataCommunicationChat.autorUsuario ? "Usted" : dataCommunicationChat.nombreAutor}
                    </strong>
                    {dataCommunicationChat.created && (
                      <><br />{formatDateEs(dataCommunicationChat.created)}</>
                    )}
                  </>
                )
              }
              crumbs={[
                { label: "Inicio", onClick: () => navigate("/inicio") },
                { label: "Comunicaciones", onClick: () => navigate("/comunicaciones") },
                { label: dataCommunicationChat?.asunto || "Detalle" },
              ]}
              actions={
                <button className="btn btn--ghost btn--sm" onClick={() => navigate(-1)}>
                  <Icon name="arrow-left" size={16} /> Volver al listado
                </button>
              }
            />
            <div className="row">
              <div className="col">
                <div className="comm-content">
                  {dataCommunicationChat && (
                    <>
                      <div className="comm-thread">
                        <CommBubble
                          msg={dataCommunicationChat}
                          kind="info"
                          isOriginal
                        />
                        {replies.map((r: any, i: number) => (
                          <CommBubble
                            key={r.id || i}
                            msg={r}
                            kind="reply"
                          />
                        ))}
                      </div>

                      {dataCommunicationChat.puedeResponder && !isImpersonating && (
                        <div data-tour="comm-reply-area">
                          {!showReply ? (
                            <div className="comm-reply-cta">
                              <button className="btn btn--primary" onClick={() => setShowReply(true)}>
                                <Icon name="plus" size={16} /> Responder
                              </button>
                            </div>
                          ) : (
                          <form className="comm-reply" onSubmit={sendReply}>
                            <div className="comm-reply__head">
                              <Icon name="chat" size={18} />
                              <span>Responder a esta comunicación</span>
                            </div>
                            <textarea
                              className="field__input field__textarea"
                              placeholder="Escribe tu respuesta..."
                              rows={5}
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              required
                            />
                            <input
                              ref={rfileRef}
                              type="file"
                              multiple
                              accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.zip,.7z,.txt,.csv,.odt,.ods,.webp"
                              style={{ display: "none" }}
                              onChange={onPick}
                            />
                            {replyFiles.length > 0 && (
                              <div className="comm-reply__files">
                                {replyFiles.map((f, i) => (
                                  <div key={i} className="comm-reply__file">
                                    <Icon name="file" size={16} />
                                    <span className="comm-reply__file-name">{f.name}</span>
                                    <button
                                      type="button"
                                      className="comm-reply__file-x"
                                      aria-label="Quitar archivo"
                                      onClick={() => setReplyFiles((prev) => prev.filter((_, j) => j !== i))}
                                    >
                                      <Icon name="x" size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="comm-reply__actions">
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => { setShowReply(false); setReplyText(""); setReplyFiles([]); }}
                              >
                                Cancelar
                              </button>
                              <button type="button" className="btn btn--secondary btn--sm" onClick={attach}>
                                <Icon name="paperclip" size={16} /> Adjuntar
                              </button>
                              <button type="submit" className="btn btn--primary btn--sm">
                                Enviar respuesta <Icon name="arrow-right" size={16} />
                              </button>
                            </div>
                          </form>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CommunicationChatGeneral;
